<script setup lang="ts">
import StfCol from '@/ui/columns/StfCol.vue';
import StfChip from '@/ui/chip/StfChip.vue';
import {useProductStore} from '@/store/good';
import type Good from '@/core/model/good';
import type GoodClass from '@/core/model/good-class';
import {storeToRefs} from 'pinia';
import {computed, nextTick, onMounted, onUnmounted, ref, type ComponentInstance} from 'vue';
import StfIcon from '@/ui/icon/StfIcon.vue';
import StfRow from '@/ui/columns/StfRow.vue';

interface Props {
    goodClass: GoodClass
    index: number;
}

const props = defineProps<Props>();
const productStore = useProductStore();
const {goods} = storeToRefs(productStore);
const showAll = ref(false);

const containerRef = ref<ComponentInstance<typeof StfRow> | null>(null);
const minimumGoodsToDisplay = ref(25);

// 1. Logic to find where the 3rd line ends
const calculateCutoff = () => {
    if (!containerRef.value) return;
    const chips = containerRef.value.$el.children;
    if (chips.length === 0) return;

    let lineCount = 1;
    let firstChipTop = (chips[0] as HTMLElement).offsetTop;
    let foundIndex = 10;
    for (let i = 0; i < chips.length; i++) {
        // If the vertical offset changes, we've moved to a new line
        if ((chips[i] as HTMLElement).offsetTop > firstChipTop) {
            lineCount++;
            firstChipTop = (chips[i] as HTMLElement).offsetTop;
        }

        // Once we hit the start of the 4th line, save that index
        if (lineCount === 4) {
            foundIndex = i;
            break;
        }
    }
    minimumGoodsToDisplay.value = foundIndex;
};

const isItemVisible = (index: number) => {
    return index < (minimumGoodsToDisplay.value + selectedGoodsInClass.value.length);
};

// 3. Lifecycle: Measure on load and when screen size changes
onMounted(async () => {
    await nextTick();
    calculateCutoff();
    window.addEventListener('resize', calculateCutoff);
});

onUnmounted(() => {
    window.removeEventListener('resize', calculateCutoff);
});

const selectedGoodsInClass = computed(() => {
    return goods.value.filter(good => good.classNumber === props.goodClass.id).map(good => good.externalId);
})

const onChipClick = (good: Good) => {    
    productStore.toggleGood(good)
}

const toggleShowAll = () => {
    showAll.value = !showAll.value;
}

</script>


<template>
    <StfRow vcenter class="my-2">
        <StfCol>{{ goodClass.name }}</StfCol>
        <StfCol class="has-text-right" col="4" col-desktop="2">
            <StfRow @click="toggleShowAll" hcenter vcenter class="stf-clickable">
                <StfCol narrow>
                    <StfBody class="bulma-is-display-inline-block"><u>{{ showAll ? 'Hide' : 'Show More' }}</u></StfBody>
                </StfCol>
                <StfCol narrow>
                    <StfIcon :icon="showAll ? 'chevronUp': 'chevronDown'"/>
                </StfCol>
            </StfRow>
        </StfCol>
    </StfRow>
    <StfRow ref="containerRef">
        <div v-for="(good, index) in goodClass.suggestions" v-show="isItemVisible(index) || showAll" :key="good.externalId" class="m-05">
            <StfChip full selectable @update:modelValue="onChipClick(good as Good)" :model-value="selectedGoodsInClass.includes(good.externalId)"  :text="good.name" bold/>
        </div>
    </StfRow>
</template>
